// ============================================================================
// DRAW SERVICE
// Handles tournament draw API calls
// ============================================================================

const API_BASE_URL = 'https://api.fsgu.co.za';

/**
 * Get draw/player list for a tournament
 * @param {number} tournamentId - Tournament ID
 * @returns {Promise<Object>} Draw data
 */
export const getDraw = async (tournamentId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/get-draw.php?tournament_id=${tournamentId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to fetch draw');
    }

    return data;
  } catch (error) {
    console.error('Error fetching draw:', error);
    throw error;
  }
};

/**
 * Import players into tournament draw (bulk)
 * @param {number} tournamentId - Tournament ID
 * @param {Array} players - Array of player objects
 * @param {boolean} replace - Replace existing draw (default true)
 * @returns {Promise<Object>} Import result
 */
export const importDraw = async (tournamentId, players, replace = true) => {
  try {
    const response = await fetch(`${API_BASE_URL}/import-draw.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tournament_id: tournamentId,
        players,
        replace,
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to import draw');
    }

    return data;
  } catch (error) {
    console.error('Error importing draw:', error);
    throw error;
  }
};

/**
 * Delete a single player from draw
 * @param {number} id - Draw entry ID
 * @returns {Promise<Object>} Delete result
 */
export const deleteDrawPlayer = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/delete-draw.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to delete player');
    }

    return data;
  } catch (error) {
    console.error('Error deleting player:', error);
    throw error;
  }
};

/**
 * Clear entire draw for a tournament
 * @param {number} tournamentId - Tournament ID
 * @returns {Promise<Object>} Delete result
 */
export const clearDraw = async (tournamentId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/delete-draw.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tournament_id: tournamentId }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to clear draw');
    }

    return data;
  } catch (error) {
    console.error('Error clearing draw:', error);
    throw error;
  }
};

/**
 * Parse CSV text into player objects
 * Expected format: player_name,club,handicap,tee_time,group_number,starting_tee
 * @param {string} csvText - Raw CSV text
 * @param {boolean} hasHeader - First row is header (default true)
 * @returns {Array} Array of player objects
 */
export const parseDrawCSV = (csvText, hasHeader = true) => {
  const lines = csvText.trim().split('\n');
  const players = [];

  const startIndex = hasHeader ? 1 : 0;

  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Handle both comma and tab delimited
    const delimiter = line.includes('\t') ? '\t' : ',';
    const parts = line.split(delimiter).map((p) => p.trim().replace(/^["']|["']$/g, ''));

    if (parts[0]) {
      players.push({
        player_name: parts[0] || '',
        club: parts[1] || '',
        handicap: parts[2] ? parseFloat(parts[2]) : null,
        tee_time: parts[3] || '',
        group_number: parts[4] ? parseInt(parts[4]) : null,
        starting_tee: parts[5] || '',
      });
    }
  }

  return players;
};

export { API_BASE_URL };